import { Status, StatusCode } from "database";
import { FC, useCallback, useMemo, useState } from "react";
import { trpc } from "@utils";
import UpdateWalletAction from "@components/wallet/action-buttons/UpdateWalletAction";
import ActionsWrapper from "@components/wallet/action-buttons/ActionsWrapper";
import { useNotification } from "hooks/useNotification";
import DeleteAction from "@components/wallet/action-buttons/DeleteAction";

interface Props {
    status: Status;
    id: string;
    name: string;
}

const FireblocksActions: FC<Props> = ({ status, id, name }) => {
    const [hasBeenClicked, setHasBeenClicked] = useState(false);
    const { invalidate: invalidateFireblocksList } =
        trpc.useContext().fireblocks;
    const notify = useNotification();

    const utils = trpc.useContext();

    const { mutate: updateWorkspace, isLoading } =
        trpc.fireblocks.update.useMutation({
            onSuccess: () => {
                invalidateFireblocksList();
            },
            onError: (error) => {
                notify.error(
                    error.data?.code === "FORBIDDEN"
                        ? error.message
                        : "Cannot process Fireblocks at the moment ",
                );
            },
            onSettled: () => {
                setHasBeenClicked(false);
            },
        });

    const actionDisabled = useMemo(() => {
        return (
            status.code === StatusCode.RUNNING ||
            status.code === StatusCode.IDLE
        );
    }, [status.code]);

    const handleUpdate = useCallback(() => {
        setHasBeenClicked(true);

        updateWorkspace({ id, hardReset: false });
    }, [id, updateWorkspace]);

    const deleteFireblocksWorkspace =
        trpc.fireblocks.deleteWorkspace.useMutation({
            onSuccess: () => {
                utils.fireblocks.list.invalidate();
            },
            onError: (err) => {
                notify.error(
                    `Failed to delete Fireblocks workspace: ${err.message}`,
                );
            },
        });

    const handleDelete = useCallback(() => {
        deleteFireblocksWorkspace.mutate({
            id,
        });
    }, [id, deleteFireblocksWorkspace]);

    return (
        <ActionsWrapper>
            <UpdateWalletAction
                disabledText="Wait for the current processing to finish before re-processing the wallet"
                handleUpdate={handleUpdate}
                disabled={actionDisabled}
                isLoading={isLoading || hasBeenClicked}
            />

            <DeleteAction
                type="wallet"
                disabled={actionDisabled}
                handleDelete={handleDelete}
                name={name}
                description="Deleting this wallet will remove its data from future cost
                    basis calculations and reports. Historical reports will
                    remain unchanged. Do you wish to proceed?"
            />
        </ActionsWrapper>
    );
};

export default FireblocksActions;
